<template>
  <div class="vector-control">
    <!-- Exclusive groups keep a single-select radio to pick the active node.
         Non-exclusive (multi-active) groups skip it and show the year layers
         directly — the surrounding collapse header already names the node. -->
    <label v-if="scope.exclusive" class="node-activate-row">
      <input
        v-sync-checked="isNodeActive"
        type="radio"
        class="node-radio"
        :name="`scope-${scope.scopeId}`"
        @change="activateNode(scope.scopeId, node.id, true)"
      />
      <span class="node-activate-label">{{ isNodeActive ? 'Active' : 'Select' }}</span>
    </label>

    <!-- Year layers: always visible for multi-active groups; gated by the active
         node when the group is exclusive. -->
    <template v-if="!scope.exclusive || isNodeActive">
      <div v-if="visibleLayers.length === 0" class="no-data">
        No data{{ selectedYears.size > 0 ? ' for selected years' : '' }}.
      </div>
      <label
        v-for="vl in visibleLayers"
        :key="vl.id"
        class="vl-row"
      >
        <input
          v-sync-checked="visibleVectorLayerIds.has(vl.id)"
          type="checkbox"
          class="vl-check"
          @change="toggleVectorLayer(vl.id, !visibleVectorLayerIds.has(vl.id))"
        />
        <span class="geom-icon">{{ geomIcon(vl.geometry_type) }}</span>
        <span class="vl-label">{{ vl.year }}</span>
      </label>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import { useLayerSelection } from '@/composables/useLayerSelection'
import type { SelectionScope } from '@/composables/layerScope'
import { vSyncChecked } from '@/directives/syncChecked'
import type { LayerTreeVectorNode } from '@/types/layerTree'

const props = defineProps<{
  node: LayerTreeVectorNode
  /** Selection scope this node belongs to (see composables/layerScope.ts). */
  scope: SelectionScope
}>()

const { visibleVectorLayerIds, selectedYears, activateNode, isNodeActive: isNodeActiveFn, toggleVectorLayer } = useLayerSelection()

const isNodeActive = computed(() => isNodeActiveFn(props.scope.scopeId, props.node.id))

// In multi-active scopes there is no explicit "Select" toggle, so keep the node
// activated — its per-year checkboxes drive what actually renders on the map.
// (Re-activates automatically if state is reset, e.g. on a farm switch.)
// allowToggleOff=false so this only ever adds, never flips the node back off.
watchEffect(() => {
  if (!props.scope.exclusive && !isNodeActive.value) {
    activateNode(props.scope.scopeId, props.node.id, false, false)
  }
})

const visibleLayers = computed(() => {
  if (!selectedYears.value.size) return props.node.vector_layers
  return props.node.vector_layers.filter(vl => selectedYears.value.has(vl.year))
})

function geomIcon(type: string) {
  const map: Record<string, string> = { point: '●', line: '╱', polygon: '▬', mixed: '⊕' }
  return map[type] ?? '⊕'
}
</script>

<style scoped>
.vector-control { display: flex; flex-direction: column; gap: 2px; padding: 2px 0; }
.no-data { font-size: 11px; color: #585b70; padding: 4px 0; }

.node-activate-row {
  display: flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  padding: 2px 4px;
}
.node-radio { accent-color: #a6e3a1; cursor: pointer; }
.node-activate-label { font-size: 11px; color: #6c7086; }

.vl-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.12s;
}
.vl-row:hover { background: rgba(137,180,250,0.07); }
.vl-check { accent-color: #a6e3a1; cursor: pointer; }
.geom-icon { font-size: 10px; width: 14px; text-align: center; color: #6c7086; }
.vl-label { font-size: 12px; color: #cdd6f4; }
</style>
